import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface BarometerGaugeProps {
  value: number;
  label: string;
  thresholdLabel: string;
  thresholdColor: string;
  strokeColor: string;
  trackColor?: string;
}

const SIZE = 160;
const STROKE = 14;
const RADIUS = (SIZE - STROKE) / 2;
const CX = SIZE / 2;
const CY = SIZE / 2;
const HALF_CIRC = Math.PI * RADIUS;
const FULL_CIRC = 2 * Math.PI * RADIUS;
const SVG_HEIGHT = CY + STROKE;

export function BarometerGauge({
  value,
  label,
  thresholdLabel,
  thresholdColor,
  strokeColor,
  trackColor = '#E8E0D8',
}: BarometerGaugeProps) {
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => setAnimatedValue(value), 100);
    return () => clearTimeout(timeout);
  }, [value]);

  const offset = HALF_CIRC * (1 - animatedValue / 100);

  return (
    <div className="flex flex-col items-center w-full">
      <svg
        width={SIZE}
        height={SVG_HEIGHT}
        viewBox={`0 0 ${SIZE} ${SVG_HEIGHT}`}
      >
        <circle
          cx={CX}
          cy={CY}
          r={RADIUS}
          fill="none"
          stroke={trackColor}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${HALF_CIRC} ${FULL_CIRC}`}
          transform={`rotate(180 ${CX} ${CY})`}
        />
        <circle
          cx={CX}
          cy={CY}
          r={RADIUS}
          fill="none"
          stroke={strokeColor}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${HALF_CIRC} ${FULL_CIRC}`}
          strokeDashoffset={offset}
          transform={`rotate(180 ${CX} ${CY})`}
          style={{
            transition: 'stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
        <motion.text
          x={CX}
          y={CY - 14}
          textAnchor="middle"
          dominantBaseline="auto"
          className="fill-chocolate-900"
          style={{ fontSize: '2rem', fontWeight: 700 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {value}%
        </motion.text>
      </svg>
      <div className="text-center mt-2">
        <div className="text-base font-semibold text-chocolate-700">{label}</div>
        <div className={`text-sm font-medium mt-0.5 ${thresholdColor}`}>
          {thresholdLabel}
        </div>
      </div>
    </div>
  );
}
